import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import SearchableSelect from 'shared/components/SearchableSelect';
import { MONITORING_TYPE, REVIEW_FREQUENCY, formatEnumLabel } from '../constants/enums';
import FormSection from './FormSection';

export const emptyMonitoringReviewMaster = () => ({
  id: null,
  monitoringType: '',
  reviewAreaControl: '',
  standardControlReference: '',
  reviewFrequency: '',
});

export default function MonitoringReviewMasterDialog({
  open,
  initialValues,
  onClose,
  onSave,
  saving = false,
}) {
  const [values, setValues] = useState(emptyMonitoringReviewMaster);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setValues({ ...emptyMonitoringReviewMaster(), ...(initialValues || {}) });
    setErrors({});
  }, [open, initialValues]);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!(values.monitoringType || '').trim()) next.monitoringType = 'Monitoring type is required';
    if (!(values.reviewAreaControl || '').trim()) {
      next.reviewAreaControl = 'Review area / control is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave?.({
      ...values,
      monitoringType: values.monitoringType.trim(),
      reviewAreaControl: values.reviewAreaControl.trim(),
      standardControlReference: (values.standardControlReference || '').trim(),
      reviewFrequency: values.reviewFrequency || undefined,
    });
  };

  const field = (name, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={values[name] ?? ''}
      onChange={(e) => setField(name, e.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name] || ''}
      disabled={saving}
      {...extra}
    />
  );

  const selectField = (name, label, options, required = false) => (
    <SearchableSelect
      size="small"
      label={label}
      value={values[name] ?? ''}
      onChange={(next) => setField(name, next)}
      error={Boolean(errors[name])}
      helperText={errors[name] || ''}
      required={required}
      disabled={saving}
      options={options.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {values.id ? 'Edit monitoring review' : 'Add monitoring review'}
      </DialogTitle>
      <DialogContent dividers>
        <FormSection title="Monitoring review details">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectField('monitoringType', 'Monitoring type', MONITORING_TYPE, true)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectField('reviewFrequency', 'Review frequency', REVIEW_FREQUENCY)}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {field('reviewAreaControl', 'Review area / control', { required: true })}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {field('standardControlReference', 'Standard control reference')}
            </Grid>
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
